import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy } from "lucide-react";

const inputCls = "mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300";
const labelCls = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

function copyText(value, label) {
  navigator.clipboard.writeText(value);
  toast.success(label || "Copied");
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function ErrBox({ children }) {
  return (
    <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="mfa-error">
      {children}
    </div>
  );
}

// Shown after /auth/mfa/enable and after /auth/mfa/recovery-codes, since both hand back
// a fresh set of 8 one-time codes that will never be shown again.
function RecoveryCodesBlock({ codes, onDone, compact }) {
  return (
    <div>
      <div className="text-sm text-slate-600 mb-3">
        Save these somewhere safe. Each code works once, and they are the only way into your account if you lose your
        phone. You will not see them again.
      </div>
      <div
        data-testid="mfa-recovery-codes"
        className={`grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-4 ${compact ? "" : "max-w-md"}`}
      >
        {codes.map((c) => (
          <div key={c}>{c}</div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={() => copyText(codes.join("\n"), "Recovery codes copied")}
          className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 flex items-center gap-1.5"
        >
          <Copy size={14} /> Copy all
        </button>
        <button type="button" onClick={onDone} className="btn-primary" data-testid="mfa-done">
          Done
        </button>
      </div>
    </div>
  );
}

// Self-contained MFA card body, shared by the agency Account page and the driver
// account page. Loads its own status and drives setup, disable, and recovery-code
// regeneration entirely through inline forms so the two account pages only need
// to drop it into a card.
export default function MfaSettings({ compact = false }) {
  const { user, applySession, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const mfaRequiredParam = new URLSearchParams(location.search).get("mfa") === "required";

  const [status, setStatus] = useState(null);
  // null | "setup-password" | "setup-qr" | "setup-codes" | "disable" | "regenerate-form" | "regenerate-codes"
  const [flow, setFlow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [setupPassword, setSetupPassword] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [enableCode, setEnableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const [regenPassword, setRegenPassword] = useState("");
  const [regenCode, setRegenCode] = useState("");

  const loadStatus = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFlow = () => {
    setFlow(null);
    setErr("");
    setSetupPassword("");
    setSetupData(null);
    setEnableCode("");
    setRecoveryCodes([]);
    setDisablePassword("");
    setDisableCode("");
    setRegenPassword("");
    setRegenCode("");
  };

  const startSetup = () => {
    resetFlow();
    setFlow("setup-password");
  };

  const continueSetup = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { password: setupPassword });
      setSetupData(data);
      setFlow("setup-qr");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitEnable = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: enableCode });
      applySession(data);
      setRecoveryCodes(data.recovery_codes || []);
      setFlow("setup-codes");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const finishSetupCodes = async () => {
    resetFlow();
    await loadStatus();
    await refreshUser();
    if (mfaRequiredParam) {
      navigate(user?.role === "driver" ? "/driver" : "/app");
    }
  };

  const startDisable = () => {
    resetFlow();
    setFlow("disable");
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/disable", { password: disablePassword, code: disableCode });
      applySession(data);
      toast.success("MFA turned off");
      resetFlow();
      await loadStatus();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const startRegenerate = () => {
    resetFlow();
    setFlow("regenerate-form");
  };

  const submitRegenerate = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/recovery-codes", { password: regenPassword, code: regenCode });
      setRecoveryCodes(data.recovery_codes || []);
      setFlow("regenerate-codes");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const finishRegenerateCodes = async () => {
    resetFlow();
    await loadStatus();
  };

  if (status === null) {
    return <div className="text-sm text-slate-500" data-testid="mfa-settings-loading">Loading…</div>;
  }

  const formCls = compact ? "space-y-3" : "space-y-4 max-w-md";

  return (
    <div data-testid="mfa-settings">
      {!status.enabled ? (
        <div>
          <p className="text-sm text-slate-600 mb-3">
            Add a second step to sign-in with an authenticator app such as Google Authenticator, Microsoft
            Authenticator or 1Password.
          </p>

          {status.required && (
            <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
              <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
              <span>{`Your agency requires MFA for your role. Set it up to continue using ${BRAND}.`}</span>
            </div>
          )}

          {flow === null && (
            <button onClick={startSetup} className="btn-primary flex items-center gap-1.5" data-testid="mfa-setup-start">
              <ShieldCheck size={14} /> Set up MFA
            </button>
          )}

          {flow === "setup-password" && (
            <form onSubmit={continueSetup} className={formCls}>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  data-testid="mfa-setup-password"
                  type="password"
                  autoComplete="current-password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="mfa-setup-continue">
                  {busy ? "Checking…" : "Continue"}
                </button>
                <button type="button" onClick={resetFlow} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {flow === "setup-qr" && setupData && (
            <form onSubmit={submitEnable} className={formCls}>
              {setupData.qr_svg ? (
                <img src={setupData.qr_svg} alt="MFA QR code" className="w-44 h-44 border rounded bg-white" />
              ) : null}
              <div>
                <div className={labelCls}>Manual entry key</div>
                <div className="mt-1 flex items-center gap-2">
                  <code data-testid="mfa-secret" className="text-sm font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-all">
                    {setupData.secret}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyText(setupData.secret, "Secret copied")}
                    className="text-xs px-2 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 flex items-center gap-1 flex-shrink-0"
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {setupData.issuer} · {user?.email}
                </div>
              </div>
              <div>
                <label className={labelCls}>6-digit code</label>
                <input
                  data-testid="mfa-enable-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={enableCode}
                  onChange={(e) => setEnableCode(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="mfa-enable-submit">
                  {busy ? "Turning on…" : "Turn on MFA"}
                </button>
                <button type="button" onClick={resetFlow} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {flow === "setup-codes" && <RecoveryCodesBlock codes={recoveryCodes} onDone={finishSetupCodes} compact={compact} />}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 mb-1">
            <ShieldCheck size={16} /> MFA is on
          </div>
          <div className="text-xs text-slate-500 mb-3">Enabled {fmtDate(status.enabled_at)}</div>
          <div className={`text-sm mb-4 ${status.recovery_codes_remaining <= 2 ? "text-amber-700 font-medium" : "text-slate-600"}`}>
            {status.recovery_codes_remaining} recovery {status.recovery_codes_remaining === 1 ? "code" : "codes"} left
          </div>

          {flow === null && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={startRegenerate}
                className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400"
                data-testid="mfa-regenerate-start"
              >
                Regenerate recovery codes
              </button>
              {status.required ? (
                <div className="text-xs text-slate-500">Required by your agency for your role.</div>
              ) : (
                <button
                  onClick={startDisable}
                  className="text-sm px-3 py-2 rounded-md border border-rose-200 text-rose-700 hover:border-rose-400"
                  data-testid="mfa-disable-start"
                >
                  Turn off MFA
                </button>
              )}
            </div>
          )}

          {flow === "regenerate-form" && (
            <form onSubmit={submitRegenerate} className={`${formCls} mt-3`}>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  data-testid="mfa-regenerate-password"
                  type="password"
                  autoComplete="current-password"
                  value={regenPassword}
                  onChange={(e) => setRegenPassword(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>6-digit code</label>
                <input
                  data-testid="mfa-regenerate-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="mfa-regenerate-submit">
                  {busy ? "Regenerating…" : "Regenerate codes"}
                </button>
                <button type="button" onClick={resetFlow} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {flow === "regenerate-codes" && (
            <div className="mt-3">
              <RecoveryCodesBlock codes={recoveryCodes} onDone={finishRegenerateCodes} compact={compact} />
            </div>
          )}

          {flow === "disable" && (
            <form onSubmit={submitDisable} className={`${formCls} mt-3`}>
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                Turning off MFA removes the second step from sign-in for this account.
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  data-testid="mfa-disable-password"
                  type="password"
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>6-digit code</label>
                <input
                  data-testid="mfa-disable-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              {err && <ErrBox>{err}</ErrBox>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="bg-rose-700 text-white px-3.5 py-2 rounded-md text-sm font-medium hover:bg-rose-800 disabled:opacity-60"
                  data-testid="mfa-disable-submit"
                >
                  {busy ? "Turning off…" : "Turn off MFA"}
                </button>
                <button type="button" onClick={resetFlow} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
